"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { jsPDF } from "jspdf"
import "svg2pdf.js"
import {
  getProjectsIframe, deleteProjectIframe, updateProjectIframeStatus,
  getKanbanColumns, addKanbanColumn, renameKanbanColumn, deleteKanbanColumn,
} from "../API"
import { supabase } from "../supabaseClient"

const IMAGES_BASE = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/"
const SVGS_BASE = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/svgs/"

const FALLBACK_COLOR = "#94a3b8"

// Pega o status gravado no pedido; se não bater com nenhuma coluna que
// existe hoje (ex: coluna foi excluída), cai na primeira coluna disponível.
function parseStatus(row, columns) {
  let stored = null
  try {
    const parsed = JSON.parse(row.order)
    if (parsed?.status) stored = parsed.status
  } catch { /* linhas antigas sem status válido */ }

  // Se o pedido foi aprovado, sempre mostra na coluna "Aprovado"
  if (isApproved(row) && columns.some(c => c.status_id === "aprovado")) {
    return "aprovado"
  }

  if (stored && columns.some(c => c.status_id === stored)) return stored
  return columns[0]?.status_id ?? "novo"
}

function isApproved(row) {
  try {
    const parsed = JSON.parse(row.order)
    return parsed?.approved === true
  } catch {
    return false
  }
}

function parseContact(row) {
  try {
    const parsed = JSON.parse(row.data)
    // Linhas antigas guardavam city/state separados; linhas novas guardam
    // address/purposes/date/list — mostramos o que existir.
    return typeof parsed === "object" && parsed ? parsed : {}
  } catch {
    return {}
  }
}

function formatDate(iso) {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    })
  } catch {
    return iso
  }
}

async function toDataURL(url) {
  const res = await fetch(url)
  const blob = await res.blob()
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function downloadJPG(row, contact) {
  if (!row.thumb) { alert("Esse pedido não tem imagem."); return }
  try {
    const dataUrl = await toDataURL(IMAGES_BASE + row.thumb)
    const a = document.createElement("a")
    a.href = dataUrl
    a.download = `pedido-${(contact.name || row.id).toString().replace(/\s+/g, "-")}.jpg`
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (e) {
    console.error("[IframeOrdersModal] erro ao baixar JPG:", e)
    alert("Não foi possível baixar a imagem.")
  }
}

// Reabre a modelagem + aplica o mapa (SVG) na UV, reaproveitando o mesmo
// mecanismo que o editor já usa pra carregar um projeto: os parâmetros de
// URL "g" (sport_id/modelo) e "t" (svg_path/mapa) — o boot do editor.js já
// sabe ler esses dois e fazer o fetch+aplicar na textura sozinho.
// Baixa o mapa (o SVG que é aplicado na UV) como PDF vetorial de verdade —
// mesma técnica que o editor.js já usa pra exportar "matriz.pdf" (jsPDF +
// svg2pdf.js), não é uma imagem raster dentro do PDF.
async function downloadVectorPDF(row, contact) {
  if (!row.svg) { alert("Esse pedido não tem mapa em SVG."); return }

  let container = null
  try {
    const res = await fetch(SVGS_BASE + row.svg)
    const svgText = await res.text()

    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgText, "image/svg+xml")
    const svgElement = svgDoc.documentElement

    if (!svgElement || svgElement.tagName.toLowerCase() !== "svg") {
      throw new Error("Conteúdo retornado não é um SVG válido.")
    }

    // Precisa estar no DOM (mesmo que invisível) pra layout ser calculado.
    container = document.createElement("div")
    container.style.position = "fixed"
    container.style.left = "-99999px"
    container.style.top = "0"
    container.appendChild(svgElement)
    document.body.appendChild(container)
    svgElement.getBoundingClientRect()

    const pdf = new jsPDF()
    await pdf.svg(svgElement, { x: 2.5, y: 50, width: 205, height: 205 })
    pdf.save(`mapa-${(contact?.name || row.id).toString().replace(/\s+/g, "-")}.pdf`)
  } catch (e) {
    console.error("[IframeOrdersModal] erro ao gerar PDF vetorial:", e)
    alert("Não foi possível gerar o PDF vetorial desse pedido.")
  } finally {
    if (container) document.body.removeChild(container)
  }
}

function openInEditor(row) {
  if (!row.sport || !row.svg) {
    alert("Esse pedido não tem modelagem e/ou mapa suficientes pra reabrir.")
    return
  }
  const url =
    `${window.location.origin}${window.location.pathname}` +
    `?g=${encodeURIComponent(row.sport)}&t=${encodeURIComponent(row.svg)}`
  window.open(url, "_blank")
}

async function downloadPDF(row, contact) {
  try {
    const doc = new jsPDF({ unit: "pt", format: "a4" })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 40
    let y = 54

    doc.setFont("helvetica", "bold")
    doc.setFontSize(18)
    doc.setTextColor("#e8272a")
    doc.text("Pedido do Simulador", margin, y)
    doc.setFont("helvetica", "normal")
    doc.setFontSize(9)
    doc.setTextColor("#888888")
    doc.text(formatDate(row.created_at), pageWidth - margin, y, { align: "right" })
    y += 12
    doc.setDrawColor("#bbbbbb")
    doc.setLineWidth(1)
    doc.line(margin, y, pageWidth - margin, y)
    y += 30

    const boxW = 170, boxH = 210
    let imagesRightEdge = margin
    let hasBack = false

    if (row.thumb) {
      try {
        const imgData = await toDataURL(IMAGES_BASE + row.thumb)
        const props = doc.getImageProperties(imgData)
        const ratio = Math.min(boxW / props.width, boxH / props.height)
        const w = props.width * ratio, h = props.height * ratio
        const format = imgData.includes("image/png") ? "PNG" : "JPEG"
        doc.addImage(imgData, format, margin, y, w, h)
        doc.setFont("helvetica", "bold")
        doc.setFontSize(8)
        doc.setTextColor("#999999")
        doc.text("FRENTE", margin, y + boxH + 14)
        imagesRightEdge = margin + boxW + 16
      } catch (e) {
        console.warn("[IframeOrdersModal] falha ao carregar imagem (frente) pro PDF:", e)
      }

      // Foto de costas é opcional — caminho derivado (thumb + "-back"),
      // pedidos antigos não vão ter, e tudo bem, só não mostra a segunda foto.
      try {
        const backImgData = await toDataURL(IMAGES_BASE + row.thumb + "-back")
        const backProps = doc.getImageProperties(backImgData)
        const backRatio = Math.min(boxW / backProps.width, boxH / backProps.height)
        const bw = backProps.width * backRatio, bh = backProps.height * backRatio
        const backFormat = backImgData.includes("image/png") ? "PNG" : "JPEG"
        doc.addImage(backImgData, backFormat, imagesRightEdge, y, bw, bh)
        doc.setFont("helvetica", "bold")
        doc.setFontSize(8)
        doc.setTextColor("#999999")
        doc.text("COSTAS", imagesRightEdge, y + boxH + 14)
        imagesRightEdge = imagesRightEdge + boxW + 16
        hasBack = true
      } catch (e) {
        // Sem foto de costas pra esse pedido — segue só com a frente.
      }
    }

    const textX = imagesRightEdge > margin ? imagesRightEdge : margin
    let ty = y + 16
    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.setTextColor("#111111")
    doc.text(contact.name || "Sem nome", textX, ty)
    ty += 24

    const field = (label, value) => {
      if (!value) return
      doc.setFont("helvetica", "normal")
      doc.setFontSize(8.5)
      doc.setTextColor("#999999")
      doc.text(label.toUpperCase(), textX, ty)
      ty += 12
      doc.setFontSize(11)
      doc.setTextColor("#111111")
      const lines = doc.splitTextToSize(String(value), pageWidth - textX - margin)
      doc.text(lines, textX, ty)
      ty += lines.length * 13 + 10
    }

    field("E-mail", contact.email)
    field("Telefone", contact.phone)
    field("Endereço", contact.address)

    y += boxH + 40
    const field2 = (label, value) => {
      if (!value) return
      doc.setFont("helvetica", "bold")
      doc.setFontSize(10)
      doc.setTextColor("#e8272a")
      doc.text(label.toUpperCase(), margin, y)
      y += 14
      doc.setFont("helvetica", "normal")
      doc.setFontSize(11)
      doc.setTextColor("#111111")
      const lines = doc.splitTextToSize(String(value), pageWidth - margin * 2)
      doc.text(lines, margin, y)
      y += lines.length * 13 + 14
    }

    field2("Finalidade", contact.purposes?.join(", "))
    field2("Data desejada", contact.date)
    field2("Lista de pedidos", contact.list)
    field2("Descrição", contact.comments)

    doc.save(`pedido-${(contact.name || row.id).toString().replace(/\s+/g, "-")}.pdf`)
  } catch (e) {
    console.error("[IframeOrdersModal] erro ao gerar PDF:", e)
    alert("Não foi possível gerar o PDF.")
  }
}

// Baixa o termo de aceite assinado (busca em iframe_approvals)
async function downloadTermoAssinado(row) {
  try {
    // Busca a aprovação vinculada a este pedido
    const { data: approval, error } = await supabase
      .from('iframe_approvals')
      .select('*')
      .eq('project_id', row.sport) // project_id = sport (ID do modelo)
      .order('created_at', { ascending: false })
      .limit(1)
      .single()

    if (error || !approval) {
      alert("Nenhum termo de aceite encontrado para este pedido.")
      return
    }

    const doc = new jsPDF({ unit: "pt", format: "a4" })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 40
    let y = margin

    // Cabeçalho
    doc.setFont("helvetica", "bold")
    doc.setFontSize(22)
    doc.setTextColor("#111111")
    doc.text("Termo de Aceite - Comprovante de Aprovação", margin, y + 10)
    y += 34
    doc.setDrawColor("#e5e5e5")
    doc.setLineWidth(1)
    doc.line(margin, y, pageWidth - margin, y)
    y += 26

    // Info da loja
    doc.setFont("helvetica", "bold")
    doc.setFontSize(14)
    doc.setTextColor("#2563eb")
    doc.text(approval.client_name || "Cliente", margin, y)
    y += 20

    doc.setFont("helvetica", "normal")
    doc.setFontSize(9)
    doc.setTextColor("#999999")
    doc.text(new Date(approval.created_at).toLocaleString("pt-BR"), pageWidth - margin, y, { align: "right" })
    y += 30

    // Mensagem do termo
    doc.setFont("helvetica", "bold")
    doc.setFontSize(10)
    doc.setTextColor("#999999")
    doc.text("MENSAGEM DE ACEITE".toUpperCase(), margin, y)
    y += 6
    doc.setDrawColor("#eeeeee")
    doc.line(margin, y, pageWidth - margin, y)
    y += 16

    doc.setFont("helvetica", "italic")
    doc.setFontSize(9.5)
    doc.setTextColor("#444444")
    const messageLines = doc.splitTextToSize(approval.message, pageWidth - margin * 2 - 10)
    doc.text(messageLines, margin + 10, y)
    y += messageLines.length * 12 + 20

    // Dados da assinatura
    doc.setFont("helvetica", "bold")
    doc.setFontSize(10)
    doc.setTextColor("#999999")
    doc.text("DADOS DA ASSINATURA".toUpperCase(), margin, y)
    y += 6
    doc.setDrawColor("#eeeeee")
    doc.line(margin, y, pageWidth - margin, y)
    y += 16

    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor("#333333")
    const infoLines = [
      `Assinado por: ${approval.client_name}`,
      approval.ip_address ? `IP de Registro: ${approval.ip_address}` : null,
      approval.device_info ? `Dispositivo: ${approval.device_info}` : null,
    ].filter(Boolean)

    infoLines.forEach((line, i) => doc.text(line, margin, y + i * 16))
    y += infoLines.length * 16 + 10

    // Assinatura
    if (approval.signature_data_url) {
      try {
        const props = doc.getImageProperties(approval.signature_data_url)
        const sigW = 130
        const sigH = (props.height / props.width) * sigW
        doc.addImage(approval.signature_data_url, "PNG", pageWidth - margin - sigW, y - 10, sigW, sigH)
        doc.setDrawColor("#cccccc")
        doc.line(pageWidth - margin - sigW, y + sigH - 2, pageWidth - margin, y + sigH - 2)
        doc.setFont("helvetica", "normal")
        doc.setFontSize(8)
        doc.setTextColor("#999999")
        doc.text("Assinatura digital", pageWidth - margin - sigW / 2, y + sigH + 8, { align: "center" })
      } catch (e) {
        console.warn("[downloadTermoAssinado] falha ao inserir assinatura:", e)
      }
    }

    doc.save(`termo-aceite-${(approval.client_name || "cliente").toString().replace(/\s+/g, "-")}.pdf`)
  } catch (e) {
    console.error("[IframeOrdersModal] erro ao gerar termo assinado:", e)
    alert("Não foi possível gerar o termo assinado.")
  }
}

export function IframeOrdersModal({ open, onOpenChange }) {
  const [orders, setOrders] = useState([])
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingColumns, setLoadingColumns] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [query, setQuery] = useState("")
  const [dragOverCol, setDragOverCol] = useState(null)
  const [editingColId, setEditingColId] = useState(null)
  const [editingLabel, setEditingLabel] = useState("")
  const [addingColumn, setAddingColumn] = useState(false)
  const [newColumnLabel, setNewColumnLabel] = useState("")

  const ownerId = typeof window !== "undefined" ? localStorage.getItem("user_id") : null

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setLoadingColumns(true)
    setQuery("")

    getProjectsIframe()
      .then((data) => setOrders(data || []))
      .catch((e) => console.error("[IframeOrdersModal] erro ao buscar pedidos:", e))
      .finally(() => setLoading(false))

    getKanbanColumns(ownerId)
      .then((data) => setColumns(data || []))
      .catch((e) => console.error("[IframeOrdersModal] erro ao buscar colunas:", e))
      .finally(() => setLoadingColumns(false))
  }, [open])

  if (!open) return null

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esse pedido?")) return
    try {
      await deleteProjectIframe(id)
      setOrders((prev) => prev.filter((o) => o.id !== id))
    } catch (e) {
      console.error("[IframeOrdersModal] erro ao excluir:", e)
      alert("Não foi possível excluir. Tente novamente.")
    }
  }

  const handleAddColumn = async () => {
    const label = newColumnLabel.trim()
    if (!label) return
    const created = await addKanbanColumn(ownerId, label, FALLBACK_COLOR, columns.length)
    if (created) {
      setColumns((prev) => [...prev, created])
      setNewColumnLabel("")
      setAddingColumn(false)
    } else {
      alert("Não foi possível criar a coluna. Tente novamente.")
    }
  }

  const startRename = (col) => {
    setEditingColId(col.id)
    setEditingLabel(col.label)
  }

  const commitRename = async (col) => {
    const label = editingLabel.trim()
    setEditingColId(null)
    if (!label || label === col.label) return
    const updated = await renameKanbanColumn(col.id, label)
    if (updated) {
      setColumns((prev) => prev.map((c) => (c.id === col.id ? updated : c)))
    } else {
      alert("Não foi possível renomear a coluna. Tente novamente.")
    }
  }

  const handleDeleteColumn = async (col) => {
    if (columns.length <= 1) {
      alert("Precisa ter pelo menos uma coluna.")
      return
    }
    const count = orders.filter((o) => parseStatus(o, columns) === col.status_id).length
    const msg = count > 0
      ? `Excluir "${col.label}"? Os ${count} pedido(s) dessa coluna vão pra "${columns.find(c => c.id !== col.id)?.label}".`
      : `Excluir a coluna "${col.label}"?`
    if (!window.confirm(msg)) return

    const ok = await deleteKanbanColumn(col.id)
    if (!ok) { alert("Não foi possível excluir a coluna."); return }

    const remaining = columns.filter((c) => c.id !== col.id)
    const fallbackCol = remaining[0]

    // Move os pedidos que estavam nessa coluna pra primeira que sobrou.
    const affected = orders.filter((o) => parseStatus(o, columns) === col.status_id)
    for (const row of affected) {
      updateProjectIframeStatus(row.id, fallbackCol.status_id)
    }
    if (affected.length > 0) {
      setOrders((prev) => prev.map((o) =>
        affected.some((a) => a.id === o.id)
          ? { ...o, order: JSON.stringify({ status: fallbackCol.status_id }) }
          : o
      ))
    }

    setColumns(remaining)
  }

  // Move um pedido pra outra coluna — atualiza a tela na hora (otimista) e
  // grava no banco em paralelo; se falhar, desfaz.
  const moveOrder = async (id, newStatus) => {
    let previousStatus = columns[0]?.status_id ?? "novo"
    setOrders((prev) => prev.map((o) => {
      if (o.id !== id) return o
      previousStatus = parseStatus(o, columns)
      return { ...o, order: JSON.stringify({ status: newStatus }) }
    }))

    const saved = await updateProjectIframeStatus(id, newStatus)
    if (!saved) {
      // Desfaz se não conseguiu salvar
      setOrders((prev) => prev.map((o) =>
        o.id === id ? { ...o, order: JSON.stringify({ status: previousStatus }) } : o
      ))
      alert("Não foi possível mover o pedido. Tente novamente.")
    }
  }

  const openWhatsApp = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "")
    if (!digits) return
    const full = digits.startsWith("55") ? digits : `55${digits}`
    window.open(`https://wa.me/${full}`, "_blank")
  }

  const normalizedQuery = query.trim().toLowerCase()
  const queryIsId = normalizedQuery.replace(/^#/, "")

  const filteredOrders = orders.filter((row) => {
    if (!normalizedQuery) return true
    const contact = parseContact(row)
    const haystack = [
      String(row.id),
      contact.name, contact.email, contact.phone, contact.address,
      contact.list, contact.comments,
    ].filter(Boolean).join(" ").toLowerCase()
    return haystack.includes(normalizedQuery) || String(row.id) === queryIsId
  })

  const actionBtn = "flex items-center justify-center gap-1 text-[10px] font-medium bg-secondary/40 hover:bg-secondary/70 rounded-lg py-1.5 transition-colors"

  return createPortal(
    <div
      className="bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ position: "fixed", inset: 0, zIndex: 2147483647 }}
      onClick={() => onOpenChange(false)}
    >
      <div
        className="bg-background text-primary w-full max-w-[1600px] rounded-2xl border border-border/50 shadow-2xl"
        style={{ display: "flex", flexDirection: "column", height: "92vh", maxHeight: "92vh", overflow: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 justify-between px-6 py-4 border-b border-border/30 shrink-0 bg-secondary/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center text-lg shrink-0">
              📋
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold leading-none">Pedidos do Iframe</h2>
                {!loading && (
                  <span className="text-[10px] font-semibold bg-primary/15 text-primary rounded-full px-2 py-0.5">
                    {filteredOrders.length}
                  </span>
                )}
              </div>
              <p className="text-xs opacity-60 mt-1">
                Arraste os cards entre as colunas pra mudar o status — clique no nome da coluna pra renomear
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:ml-auto">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-40">🔎</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome, e-mail, telefone ou #id..."
                className="w-full sm:w-72 text-xs bg-secondary/30 border border-border/40 rounded-lg pl-8 pr-3 py-2 outline-none focus:border-primary/50 placeholder:opacity-50"
              />
            </div>
            <button
              className="w-8 h-8 rounded-lg bg-secondary/60 hover:bg-secondary flex items-center justify-center shrink-0"
              onClick={() => onOpenChange(false)}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content — Kanban */}
        <div
          className="p-4"
          style={{ flex: "1 1 auto", minHeight: 0, overflowX: "auto", overflowY: "hidden" }}
        >
          {(loading || loadingColumns) && (
            <div className="text-center py-16 opacity-60 text-sm">Carregando pedidos...</div>
          )}

          {!loading && !loadingColumns && orders.length === 0 && (
            <div className="text-center py-16 opacity-60 text-sm">
              Nenhum pedido recebido pelo iframe ainda.
            </div>
          )}

          {!loading && !loadingColumns && orders.length > 0 && filteredOrders.length === 0 && (
            <div className="text-center py-16 opacity-60 text-sm">
              Nenhum pedido encontrado pra "{query}".
            </div>
          )}

          {!loading && !loadingColumns && filteredOrders.length > 0 && (
            <div style={{ display: "flex", gap: 12, height: "100%", minWidth: "max-content", minHeight: 0 }}>
              {columns.map((col) => {
                const colOrders = filteredOrders.filter((row) => parseStatus(row, columns) === col.status_id)
                const isDragOver = dragOverCol === col.status_id

                return (
                  <div
                    key={col.id}
                    className="rounded-xl bg-secondary/10 border transition-colors"
                    style={{
                      display: "flex", flexDirection: "column",
                      width: 300, minWidth: 300, height: "100%", minHeight: 0,
                      borderColor: isDragOver ? col.color : "rgba(255,255,255,.08)",
                      background: isDragOver ? `${col.color}14` : undefined,
                    }}
                    onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.status_id) }}
                    onDragLeave={() => setDragOverCol((prev) => (prev === col.status_id ? null : prev))}
                    onDrop={(e) => {
                      e.preventDefault()
                      setDragOverCol(null)
                      const id = Number(e.dataTransfer.getData("text/plain"))
                      if (id) moveOrder(id, col.status_id)
                    }}
                  >
                    {/* Column header */}
                    <div
                      className="group/col gap-2 px-3 py-3 border-b border-border/20"
                      style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
                    >
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: col.color }} />

                      {editingColId === col.id ? (
                        <input
                          autoFocus
                          value={editingLabel}
                          onChange={(e) => setEditingLabel(e.target.value)}
                          onBlur={() => commitRename(col)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename(col)
                            if (e.key === "Escape") setEditingColId(null)
                          }}
                          className="text-xs font-bold flex-1 bg-secondary/40 border border-primary/40 rounded px-1 py-0.5 outline-none"
                        />
                      ) : (
                        <span
                          className="text-xs font-bold flex-1 cursor-text hover:opacity-80"
                          title="Clique pra renomear"
                          onClick={() => startRename(col)}
                        >
                          {col.label}
                        </span>
                      )}

                      <span className="text-[10px] font-semibold opacity-60 bg-secondary/40 rounded-full px-1.5 py-0.5">
                        {colOrders.length}
                      </span>

                      <button
                        title="Excluir coluna"
                        className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] opacity-0 group-hover/col:opacity-60 hover:!opacity-100 hover:bg-red-600/70 hover:text-white transition-all"
                        onClick={() => handleDeleteColumn(col)}
                      >
                        ✕
                      </button>
                    </div>

                    {/* Cards */}
                    <div
                      className="p-2 space-y-2"
                      style={{
                        flex: "1 1 auto", minHeight: 0,
                        overflowY: "scroll", overflowX: "hidden",
                        touchAction: "pan-y",
                        overscrollBehavior: "contain",
                      }}
                      onWheel={(e) => {
                        // Reforço manual: move o scroll na mão, caso algo no
                        // CSS global do site esteja atrapalhando o
                        // overflow-y nativo (ex: overflow:hidden !important
                        // em algum lugar que eu não tenho como ver).
                        e.currentTarget.scrollTop += e.deltaY
                      }}
                    >
                      {colOrders.length === 0 && (
                        <div className="text-center text-[11px] opacity-40 py-6 border border-dashed border-border/30 rounded-lg">
                          Arraste um card pra cá
                        </div>
                      )}

                      {colOrders.map((row) => {
                        const contact = parseContact(row)
                        const thumbUrl = row.thumb ? IMAGES_BASE + row.thumb : null
                        const isExpanded = expanded === row.id
                        const status = parseStatus(row, columns)

                        return (
                          <div
                            key={row.id}
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData("text/plain", String(row.id))}
                            className="group rounded-xl border border-border/40 bg-secondary/20 overflow-hidden flex flex-col transition-all hover:border-primary/40 hover:shadow-lg cursor-grab active:cursor-grabbing"
                          >
                            <div
                              className="relative aspect-video bg-secondary/30 cursor-pointer"
                              onClick={() => setExpanded(isExpanded ? null : row.id)}
                            >
                              {thumbUrl ? (
                                <img src={thumbUrl} alt={contact.name || "Pedido"} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-3xl opacity-30">👕</div>
                              )}
                              <span className="absolute top-1.5 left-1.5 text-[10px] font-bold bg-black/70 text-white rounded-full px-2 py-0.5 backdrop-blur-sm">
                                #{row.id}
                              </span>
                              {isApproved(row) && (
                                <span className="absolute top-1.5 right-9 text-[9px] font-bold bg-green-500/90 text-white rounded-full px-2 py-0.5 backdrop-blur-sm flex items-center gap-1">
                                  ✓ Aprovado
                                </span>
                              )}
                              <button
                                title="Excluir"
                                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-red-600 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => { e.stopPropagation(); handleDelete(row.id) }}
                              >
                                ✕
                              </button>
                            </div>

                            <div className="p-2.5 flex flex-col gap-1">
                              <span className="font-semibold text-xs line-clamp-1">{contact.name || "Sem nome"}</span>
                              <span className="text-[10px] opacity-50">{formatDate(row.created_at)}</span>

                              {contact.email && <span className="text-[11px] opacity-80 line-clamp-1">✉️ {contact.email}</span>}

                              {isExpanded && (
                                <div className="mt-1 pt-2 border-t border-border/30 space-y-1.5">
                                  {contact.address && <p className="text-[11px]"><b>Endereço:</b> {contact.address}</p>}
                                  {contact.purposes?.length > 0 && (
                                    <p className="text-[11px]"><b>Finalidade:</b> {contact.purposes.join(", ")}</p>
                                  )}
                                  {contact.date && <p className="text-[11px]"><b>Data desejada:</b> {contact.date}</p>}
                                  {contact.list && (
                                    <div className="text-[11px]">
                                      <b>Lista de pedidos:</b>
                                      <p className="whitespace-pre-wrap opacity-80 mt-0.5">{contact.list}</p>
                                    </div>
                                  )}
                                  {contact.comments && (
                                    <div className="text-[11px]">
                                      <b>Descrição:</b>
                                      <p className="whitespace-pre-wrap opacity-80 mt-0.5">{contact.comments}</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              <button
                                className="text-[10px] opacity-60 hover:opacity-100 text-left"
                                onClick={() => setExpanded(isExpanded ? null : row.id)}
                              >
                                {isExpanded ? "▲ Ver menos" : "▼ Ver detalhes"}
                              </button>

                              {/* Seletor de status — alternativa ao arrastar, funciona no touch */}
                              <select
                                value={status}
                                onChange={(e) => moveOrder(row.id, e.target.value)}
                                className="text-[10px] bg-secondary/40 border border-border/40 rounded-lg px-2 py-1.5 mt-1 outline-none"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {columns.map((s) => (
                                  <option key={s.id} value={s.status_id}>{s.label}</option>
                                ))}
                              </select>

                              <div className="grid grid-cols-2 gap-1 mt-1">
                                {contact.phone && (
                                  <button
                                    className={`${actionBtn} bg-green-600/80 hover:bg-green-600 text-white col-span-2`}
                                    onClick={() => openWhatsApp(contact.phone)}
                                  >
                                    💬 WhatsApp
                                  </button>
                                )}
                                <button
                                  title="Abrir a modelagem com o mapa aplicado, no editor"
                                  className={actionBtn}
                                  onClick={() => openInEditor(row)}
                                >
                                  🧩 Editor
                                </button>
                                <button className={actionBtn} onClick={() => downloadJPG(row, contact)}>
                                  🖼️ JPG
                                </button>
                                <button
                                  title="Resumo do pedido em PDF"
                                  className={actionBtn}
                                  onClick={() => downloadPDF(row, contact)}
                                >
                                  📄 Resumo
                                </button>
                                <button
                                  title="Mapa (SVG na UV) em PDF vetorial"
                                  className={actionBtn}
                                  onClick={() => downloadVectorPDF(row, contact)}
                                >
                                  ✒️ Vetor
                                </button>
                                {(() => {
                                  try {
                                    const orderData = JSON.parse(row.order);
                                    if (orderData.approval_id) {
                                      return (
                                        <button
                                          title="Baixar termo de aceite assinado pelo cliente"
                                          className={`${actionBtn} bg-blue-600/80 hover:bg-blue-600 text-white`}
                                          onClick={() => downloadTermoAssinado(row)}
                                        >
                                          📝 Termo Assinado
                                        </button>
                                      );
                                    }
                                  } catch (e) {}
                                  return null;
                                })()}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}

              {/* Nova coluna */}
              <div
                className="rounded-xl border border-dashed border-border/30 flex-shrink-0"
                style={{ width: 240, minWidth: 240, height: "100%" }}
              >
                {addingColumn ? (
                  <div className="p-3 flex flex-col gap-2">
                    <input
                      autoFocus
                      value={newColumnLabel}
                      onChange={(e) => setNewColumnLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddColumn()
                        if (e.key === "Escape") { setAddingColumn(false); setNewColumnLabel("") }
                      }}
                      placeholder="Nome da coluna"
                      className="text-xs bg-secondary/40 border border-border/40 rounded-lg px-2 py-2 outline-none focus:border-primary/50"
                    />
                    <div className="flex gap-2">
                      <button
                        className="flex-1 text-[11px] font-semibold bg-primary text-primary-foreground rounded-lg py-1.5"
                        onClick={handleAddColumn}
                      >
                        Adicionar
                      </button>
                      <button
                        className="flex-1 text-[11px] bg-secondary/40 hover:bg-secondary/70 rounded-lg py-1.5"
                        onClick={() => { setAddingColumn(false); setNewColumnLabel("") }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="w-full h-full flex items-center justify-center gap-2 text-xs opacity-50 hover:opacity-90 transition-opacity"
                    onClick={() => setAddingColumn(true)}
                  >
                    + Nova coluna
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}